import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import type { UseFormReturn } from 'react-hook-form';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { MultiSelect } from '@/components/ui/multi-select';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ScrollPage } from '@/components/layout/PageContainer';
import { useDialers } from '@/features/dialers/api';
import type { GroupFormInput } from '../schema';

interface GroupFormProps {
  mode: 'create' | 'update';
  form: UseFormReturn<GroupFormInput>;
  onSubmit: (input: GroupFormInput) => void;
  isPending: boolean;
  pageTitle: string;
  pageDescription: string;
}

/**
 * Shared form chrome for Create/Update Group. Each mode constructs its own
 * react-hook-form (different defaults / prefill paths) and passes it in.
 */
export function GroupForm({
  mode,
  form,
  onSubmit,
  isPending,
  pageTitle,
  pageDescription,
}: GroupFormProps) {
  const dialers = useDialers();
  const dialerOptions = useMemo(
    () =>
      (dialers.data ?? []).map((d) => ({
        value: String(d.dialerId),
        label: d.dialerName,
      })),
    [dialers.data],
  );

  return (
    <ScrollPage>
      <PageHeader
        title={pageTitle}
        description={pageDescription}
        actions={
          <Button asChild variant="outline">
            <Link to="/group-list">
              <ArrowLeft className="h-4 w-4" />
              Back to groups
            </Link>
          </Button>
        }
      />

      <Form {...form}>
        <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)} noValidate>
          <Card>
            <CardHeader>
              <CardTitle>Group details</CardTitle>
              <CardDescription>
                Pick a unique name and the dialers this group should target.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="groupName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Group name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g. North-zone-dialers" autoFocus={mode === 'create'} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="dialerIds"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Dialers</FormLabel>
                    <FormControl>
                      <MultiSelect
                        options={dialerOptions}
                        value={field.value}
                        onChange={field.onChange}
                        placeholder={
                          dialers.isLoading ? 'Loading dialers…' : 'Pick dialers for this group'
                        }
                        searchPlaceholder="Filter dialers…"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <div className="flex items-center justify-end gap-2">
            <Button asChild variant="ghost">
              <Link to="/group-list">Cancel</Link>
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {mode === 'create' ? 'Create group' : 'Save changes'}
            </Button>
          </div>
        </form>
      </Form>
    </ScrollPage>
  );
}

